// app/javascript/controllers/sticky_controller.js
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["nav"];
  
  connect() {
    this.originalOffsetTop = this.navTarget.offsetTop;
    this.observer = null;
    this.visibleSections = new Map();
    this.currentSectionId = '';
    this.debounceTimer = null
    this.debounceDelay = 100
    
    window.addEventListener("scroll", this.stick.bind(this));
    this.setupIntersectionObserver();
  }

  disconnect() {
    window.removeEventListener("scroll", this.stick.bind(this));
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  isInViewport(element) {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    return (
      rect.top <= (window.innerHeight * 0.9) && // 90% of viewport height
      rect.bottom >= 0
    );
  }

  stick() {
    const isMobile = window.innerWidth < 768;
    const stickyHotelInfo = document.getElementById('sticky-hotel-info');
    const isFooterInView = this.isInViewport(document.querySelector('footer'));
    
    if (window.scrollY > this.originalOffsetTop) {
      this.navTarget.classList.add("fixed");
      if (!isMobile && stickyHotelInfo) {
        stickyHotelInfo.classList.remove("hidden");
      }
    } else {
      this.navTarget.classList.remove("fixed");
      if (!isMobile && stickyHotelInfo) {
        stickyHotelInfo.classList.add("hidden");
      }
    }

    if (stickyHotelInfo && isFooterInView) {
      stickyHotelInfo.classList.add("hidden");
    }
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: this.buildThresholdList()
    };

    this.observer = new IntersectionObserver(this.handleIntersect.bind(this), options);
    document.querySelectorAll('[data-section="true"]').forEach(section => {
      this.observer.observe(section);
    });
  }

  buildThresholdList() {
    const thresholds = [];
    const numSteps = 20;
    
    for (let i=1.0; i<=numSteps; i++) {
      const ratio = i/numSteps;
      thresholds.push(ratio);
    }
    
    thresholds.push(0);
    return thresholds;
  }

  handleIntersect(entries) {
    entries.forEach(entry => {
      const sectionId = entry.target.id;
      
      if (entry.isIntersecting) {
        this.visibleSections.set(sectionId, entry.intersectionRatio);
      } else {
        this.visibleSections.delete(sectionId);
      }
    });

    clearTimeout(this.debounceTimer)

    this.debounceTimer = setTimeout(() => {
      this.updateActiveSection();
    }, this.debounceDelay)
  }

  updateActiveSection() {
    if (this.visibleSections.size === 0) return;
  
    // Get ordered section IDs from nav tabs
    const tabs = this.navTarget.querySelectorAll('[data-slot="tab"]');
    const dataKeys = Array.from(tabs).map(tab => tab.getAttribute('data-key'));
  
    let activeSectionId = '';
    let maxRatio = -1;
    let maxSectionId = '';
  
    // Find the first section in order with ratio >= 0.75
    for (const sectionId of dataKeys) {
      const ratio = this.visibleSections.get(sectionId) || 0;
      if (ratio >= 0.75) {
        activeSectionId = sectionId;
        break;
      }
      if (ratio > maxRatio) {
        maxRatio = ratio;
        maxSectionId = sectionId;
      }
    }
  
    // If no section met the threshold, use the most visible one
    if (!activeSectionId) {
      activeSectionId = maxSectionId;
    }
  
    if (activeSectionId && activeSectionId !== this.currentSectionId) {
      this.currentSectionId = activeSectionId;
      this.updateTabs();
    }
  }

  updateTabs() {
    const tabs = this.navTarget.querySelectorAll('[data-slot="tab"]');
    
    tabs.forEach(tab => {
      const tabSectionId = tab.getAttribute('data-key');
      if (tabSectionId === this.currentSectionId) {
        const scrollableNavbar = document.getElementById('scrollable-navbar');
        if (scrollableNavbar && !isElementInViewport(tab, scrollableNavbar)) {
          tab.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
        tab.setAttribute('data-selected', 'true');
        tab.setAttribute('tabindex', '0');
      } else {
        tab.setAttribute('data-selected', 'false');
        tab.setAttribute('tabindex', '-1');
      }
    });
  }

  selectTab(event) {
    const selectedTab = event.currentTarget;
    const tabs = this.navTarget.querySelectorAll('[data-slot="tab"]');
    const sectionId = selectedTab.getAttribute('data-key');

    tabs.forEach(tab => {
      tab.setAttribute('data-selected', 'false');
      tab.setAttribute('tabindex', '-1');
    });

    selectedTab.setAttribute('data-selected', 'true');
    selectedTab.setAttribute('tabindex', '0');

    const section = document.getElementById(sectionId);
    if (section) {
      // Update URL fragment
      window.history.pushState({}, '', `#${sectionId}`);
      
      // Calculate the position with offset
      const topPos = section.getBoundingClientRect().top + window.pageYOffset - 50;

      // Scroll to the position with smooth behavior
      window.scrollTo({
        top: topPos,
        behavior: 'smooth'
      });
    }
  }

}

// Helper to check if tab is fully visible inside scrollableNavbar
function isElementInViewport(el, container) {
  const elRect = el.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  return (
    elRect.left >= containerRect.left &&
    elRect.right <= containerRect.right
  );  
}